import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Button, useMediaQuery, Grid } from "@mui/material";
import moment from "moment";

import Backdrop from "Components/Common/Backdrop";
import BreadCrumb from "Components/Common/BreadCrumb";
import InputFieldText from "Components/Common/InputFieldText";
import DatePicker from "Components/Common/DatePicker";
import BaseTable from "Components/Common/BaseTable";
import SelectField from "Components/Common/SelectField";
import { outStandingReportBillingSubTypes } from "common/enums";

import { convertCamelTextToCapitalizeText } from "utils/convertCamelTextToCapitalizeText";
import { dateFormatter } from "utils/dateFormatter";
import { isAuthorizedUser } from "utils";

import { useLazyGetOutstandingReportsQuery } from "apis/Payment";
import { useLazyDownloadExcelQuery } from "slices/api";

const RECORDS_PER_PAGE = 20;

const getTableColumns = (columns, columnData = {}, fieldKey, valueKey) => {
  Object.keys(columnData).forEach((key) => {
    columns.push({
      [fieldKey]: convertCamelTextToCapitalizeText(key),
      [valueKey]: key,
    });
  });
};

const OutstandingReport = ({ t }) => {
  const [downloadExcel, { isLoading: isExcelFileDownloading }] =
    useLazyDownloadExcelQuery();
  const resetPageRef = useRef();

  const [query, setQuery] = useState({
    year: moment(),
    consumerNumber: "",
    subscriptionType: "",
  });
  const [consumerNumber, setConsumerNumber] = useState("");
  const [subscriptionType, setSubscriptionType] = useState("");
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [year, setYear] = useState(moment());

  const sm = useMediaQuery("(max-width: 599px)");
  const { roles } = useSelector((state) => state.Login);

  const [getOutstandingData, { data: { data: reportsData } = {}, isLoading }] =
    useLazyGetOutstandingReportsQuery();

  const updatedReportsData = useMemo(
    () =>
      reportsData?.map(
        ({ consumerNumber, consumerName, totalOutStanding, months }) => {
          const obj = { consumerNumber, consumerName, totalOutStanding };
          months.forEach(({ month, outStandingDues }) => {
            obj[month] = outStandingDues;
          });
          return obj;
        }
      ) ?? [],
    [reportsData]
  );

  const columns = useMemo(() => {
    const tableColumns = [];
    getTableColumns(tableColumns, updatedReportsData?.[0], "title", "field");
    return tableColumns;
  }, [updatedReportsData]);

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const updatedBillingSubscriptionOptions = useMemo(
    () =>
      outStandingReportBillingSubTypes.filter(({ allowedRoles }) =>
        isAuthorizedUser(roles, allowedRoles)
      ),
    [outStandingReportBillingSubTypes]
  );

  useEffect(() => {
    if (updatedBillingSubscriptionOptions.length && !subscriptionType) {
      setQuery((prev) => ({
        ...prev,
        subscriptionType: updatedBillingSubscriptionOptions[0].value,
      }));
      setSubscriptionType(updatedBillingSubscriptionOptions[0].value);
    }
  }, [updatedBillingSubscriptionOptions]);

  useEffect(() => {
    if (query.subscriptionType) {
      getOutstandingData({
        reportYear: query.year.format("YYYY"),
        consumerNumber: query.consumerNumber,
        subscriptionType: query.subscriptionType,
      });
    }
  }, [query]);

  return (
    <>
      <Backdrop open={isExcelFileDownloading || isLoading} />

      <BreadCrumb title={t("Outstanding Report")} pageTitle={t("Payment")} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleResetPage();
          setQuery({
            year,
            consumerNumber,
            subscriptionType,
          });
        }}
      >
        <Grid container columnSpacing={2} className="mb-1">
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <SelectField
              label={t("Subscription Type") + "*"}
              onChange={(_, { value }) => setSubscriptionType(value)}
              value={
                updatedBillingSubscriptionOptions.find(
                  ({ value }) => value === subscriptionType
                ) ?? ""
              }
              options={updatedBillingSubscriptionOptions}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              slotProps={{
                textField: {
                  size: "small",
                },
              }}
              label={t("Select Year")}
              className="w-100"
              views={["year"]}
              format={dateFormatter(year)}
              value={year}
              disableFuture
              onChange={(date) => setYear(date)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InputFieldText
              placeholder={t("Consumer Number")}
              value={consumerNumber}
              type="number"
              onChange={(e) => setConsumerNumber(e.target.value)}
              label={t("Consumer Number")}
            />
          </Grid>

          <Grid item>
            <Button
              variant="outlined"
              className="mb-2 me-2"
              type="submit"
              sx={{ maxHeight: "40px" }}
            >
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              className="mb-2 me-2"
              onClick={() => {
                handleResetPage();
                setConsumerNumber("");
                setQuery((prev) => ({
                  year: prev.year,
                  consumerNumber: "",
                  subscriptionType: updatedBillingSubscriptionOptions[0].value,
                }));
                setSubscriptionType(updatedBillingSubscriptionOptions[0].value);
              }}
              sx={{ maxHeight: "40px" }}
            >
              {t("Clear")}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              className="mb-2 me-2"
              onClick={async () =>
                await downloadExcel(
                  {
                    endpoint: "/report/outstanding-details-of-consumer-excel",
                    sheetName: "Outstanding Report",
                    query: {
                      reportYear: query.year.format("YYYY"),
                      subscriptionType,
                    },
                  },
                  true
                )
              }
              sx={{ maxHeight: "40px" }}
            >
              {t("Download")}
            </Button>
          </Grid>
        </Grid>
      </form>
      <BaseTable
        columns={columns}
        data={updatedReportsData}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        pageSize={RECORDS_PER_PAGE}
        maxHeight={sm ? "calc(100vh - 300px)" : "calc(100vh - 260px)"}
      />
    </>
  );
};

OutstandingReport.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(OutstandingReport);
